import { CommonModule } from '@angular/common';
import { Component, Input, ChangeDetectionStrategy } from '@angular/core';
import { GenVideoPreviewData } from '../../../../../../../models/google-template/preview-interfaces/gen-video-preview-data.interface';
import { UrlDisplayHelper } from '../../../../../../../utils/url-display-helper';
import { VideoFormat, YoutubeFormatHelper } from '../../../../../../../utils/youtube-format-helper';
import { TvFrameComponent } from '../../../preview-common/tv-frame/tv-frame.component';

/**
 * Skippable in-stream ad on the TV watch page. The creative does not fill the
 * screen: the set gives it the left two thirds and puts a QR code beside it,
 * because a television has no clickable surface — the viewer scans instead.
 *
 * The footer under the pair is the ad's own, carrying the advertiser, the
 * destination and the two remotes controls the viewer has.
 */
@Component({
    selector: 'app-gen-youtube-watch-ad-tv',
    imports: [CommonModule, TvFrameComponent],
    templateUrl: './youtube-watch-ad-tv.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrl: './youtube-watch-ad-tv.component.scss'
})
export class YoutubeWatchAdTvPreviewComponent {
  @Input() data!: GenVideoPreviewData;

  /** The creative format this placement previews, found by measured ratio. */
  readonly format: VideoFormat = '16:9';

  /** Thumbnail of the 16:9 video, or '' when there isn't one yet. */
  get thumbnailUrl(): string {
    return YoutubeFormatHelper.thumbnailForFormat(this.videoIds, this.format);
  }

  /**
   * True while any link is still being measured, so the empty player says so
   * rather than asking for a video that is about to turn up.
   */
  get isResolving(): boolean {
    return YoutubeFormatHelper.isResolvingAny(this.videoIds);
  }

  /** The host, which is all the footer shows of the destination. */
  get displayUrl(): string {
    return UrlDisplayHelper.hostOf(this.data.url);
  }

  /**
   * The footer gives the headline one line beside the buttons, so it takes the
   * short one and only falls back to the long one when there isn't one.
   */
  get headline(): string {
    return this.data.headlines[0] || this.data.longHeadlines[0] || 'Your headline appears here';
  }

  private get videoIds(): string[] {
    return this.data.videos.map((video) => video.videoId);
  }
}
